import { Button, Tag } from '@carbon/react';
import type {
  MigrationCandidate,
  MigrationSuggestion as Suggestion,
  ScenarioChoice,
} from './types';
import { areaTagFields } from './utils';
import styles from './case-editor.module.scss';

/**
 * A suggestion or candidate as the tile's pick.
 *
 * @param {MigrationCandidate} candidate - The scenario the migration names.
 * @return {ScenarioChoice} The choice.
 */
export function candidateChoice(candidate: MigrationCandidate): ScenarioChoice {
  return {
    id: candidate.id,
    name: candidate.name ?? `#${candidate.id}`,
    ...areaTagFields(candidate),
  };
}

interface MigrationSuggestionProps {
  suggestion: Suggestion;
  /** Picks a scenario for the domain, as the picker does. */
  onPick: (choice: ScenarioChoice) => void;
}

/**
 * What the legacy-case migration suggests for an empty domain tile (11.4): "Suggested from
 * migration", the suggested scenario and why, with one click to use it, and the other scenarios
 * the case's areas used, each one click too. A pick fills the tile like the picker's; saving it
 * clears the suggestion (EvaluationCase#clear_confirmed_migration_suggestions), as picking any
 * other scenario through the picker does.
 *
 * @param {MigrationSuggestionProps} props - The suggestion and the pick handler.
 * @return {JSX.Element} The suggestion block.
 */
export function MigrationSuggestion({
  suggestion,
  onPick,
}: MigrationSuggestionProps) {
  const suggested = suggestedCandidate(suggestion);
  // A scenario deleted since can't be assigned, and isn't shown.
  const candidates = suggestion.candidates.filter(
    (candidate) => candidate.name !== null
  );

  return (
    <div
      className={styles.suggestion}
      data-testid={`migration-suggestion-${suggestion.domain}`}
    >
      <span className={styles.suggestionLabel}>Suggested from migration</span>
      <span className={styles.tileScenario}>
        {suggested.name ?? 'A deleted scenario'}
        {suggested.archived && <ArchivedTag />}
      </span>
      {suggestion.reason && (
        <span className={styles.suggestionReason}>{suggestion.reason}</span>
      )}
      {pickable(suggested) && (
        <Button
          kind="tertiary"
          size="sm"
          onClick={() => onPick(candidateChoice(suggested))}
        >
          Use {suggested.name}
        </Button>
      )}
      {candidates.length > 0 && (
        <div className={styles.suggestionCandidates}>
          <span className={styles.suggestionLabel}>Other candidates</span>
          {candidates.map((candidate) => {
            if (!pickable(candidate)) {
              return (
                <span key={candidate.id} className={styles.tileScenario}>
                  {candidate.name}
                  <ArchivedTag />
                </span>
              );
            }
            return (
              <Button
                key={candidate.id}
                kind="ghost"
                size="sm"
                onClick={() => onPick(candidateChoice(candidate))}
              >
                Use {candidate.name}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const ArchivedTag = () => (
  <Tag size="sm" type="red">
    Archived
  </Tag>
);

/**
 * The suggestion's own scenario, as a candidate.
 *
 * @param {Suggestion} suggestion - The suggestion.
 * @return {MigrationCandidate} Its scenario.
 */
export function suggestedCandidate(suggestion: Suggestion): MigrationCandidate {
  return {
    id: suggestion.scenarioId,
    name: suggestion.scenarioName,
    ...areaTagFields(suggestion),
    archived: suggestion.archived,
  };
}

/**
 * Whether a scenario the migration names can be picked: it still exists and isn't archived (a
 * case can't be saved with an archived scenario, EvaluationCase#assigned_scenarios_active).
 *
 * @param {MigrationCandidate} candidate - The scenario.
 * @return {boolean} Whether it can be picked.
 */
export function pickable(candidate: MigrationCandidate): boolean {
  return candidate.name !== null && !candidate.archived;
}
